import { useCallback, useEffect, useRef, useState } from 'react';

export interface RetriedLoadOptions {
  /** Total attempts per load cycle (the first try plus retries). Default 3. */
  attempts?: number;
  /** Delay before the first retry; it doubles on each further retry. Default 500ms. */
  baseDelayMs?: number;
}

export interface RetriedLoad<T> {
  /** The last successful result, or null until the first success. */
  data: T | null;
  /** Set only once every attempt of a load cycle has failed. */
  error: string | null;
  /** True while a load cycle (including its retry waits) is in flight. */
  loading: boolean;
  /** Start a fresh load cycle. Clears `error`; keeps the previous `data`. */
  retry: () => void;
}

function describeError(err: unknown): string {
  if (err instanceof Error && err.message) return err.message;
  return String(err);
}

/**
 * Load a resource on mount with bounded exponential-backoff retries, and expose
 * a manual `retry` for the caller to surface when every attempt failed.
 *
 * Built for one-shot catalog loads (profiles, models) that used to run exactly
 * once in a `useEffect(..., [])` with a swallowed error: a single transient
 * miss (cold API timeout, 5xx, a refresh race after login) left the page
 * without the catalog for its whole lifetime. Here a miss is retried, and a
 * total failure is reported instead of hidden.
 *
 * `load` is read through a ref, so an inline arrow is fine: a new identity does
 * NOT restart the cycle. In-flight results and pending retries are discarded on
 * unmount, so no state is written after the component is gone.
 */
export function useRetriedLoad<T>(
  load: () => Promise<T>,
  opts: RetriedLoadOptions = {},
): RetriedLoad<T> {
  const attempts = Math.max(1, opts.attempts ?? 3);
  const baseDelayMs = opts.baseDelayMs ?? 500;

  // Latest loader, synced after each commit so the retry cycle never restarts
  // on an identity change and a manual retry always runs the newest one.
  const loadRef = useRef(load);
  useEffect(() => {
    loadRef.current = load;
  }, [load]);

  // Bumped by `retry`; each value runs one load cycle.
  const [cycle, setCycle] = useState(0);
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const attempt = (index: number) => {
      loadRef.current().then(
        (result) => {
          if (cancelled) return;
          setData(result);
          setError(null);
          setLoading(false);
        },
        (err: unknown) => {
          if (cancelled) return;
          if (index + 1 < attempts) {
            timer = setTimeout(() => attempt(index + 1), baseDelayMs * 2 ** index);
            return;
          }
          setError(describeError(err));
          setLoading(false);
        },
      );
    };

    attempt(0);
    return () => {
      cancelled = true;
      if (timer !== undefined) clearTimeout(timer);
    };
  }, [cycle, attempts, baseDelayMs]);

  const retry = useCallback(() => {
    setError(null);
    setLoading(true);
    setCycle((c) => c + 1);
  }, []);

  return { data, error, loading, retry };
}
